import { LuSlidersHorizontal, LuX, LuRotateCcw } from 'react-icons/lu'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '#/components/ui/drawer'
import { Button } from '#/components/ui/button'
import BlogsFilter from './BlogsFilter'
import { cn } from '#/lib/utils'
import type { Tag } from '#/features/courses/types/course.types'

interface BlogsFilterDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tags: Tag[]
  activeTags: string[]
  onToggleTag: (slug: string) => void
  onClearTags: () => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
  activeFilterCount: number
  onClearAll: () => void
}

export default function BlogsFilterDrawer({
  open,
  onOpenChange,
  tags,
  activeTags,
  onToggleTag,
  onClearTags,
  searchQuery = '',
  onSearchChange,
  activeFilterCount,
  onClearAll,
}: BlogsFilterDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerTrigger asChild>
        <Button
          variant={activeFilterCount > 0 ? 'primary' : 'outline'}
          className={cn(
            'relative flex h-11 shrink-0 items-center gap-2 rounded-xl px-4 text-sm font-semibold transition-all shadow-xs',
            activeFilterCount > 0
              ? 'bg-secondary-600 text-white hover:bg-secondary-700 border-secondary-600 shadow-secondary-200'
              : 'bg-white border-neutral-200 text-neutral-800 hover:bg-neutral-50 hover:border-neutral-300',
          )}
        >
          <LuSlidersHorizontal className="h-4 w-4" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[11px] font-bold text-neutral-900 shadow-xs">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </DrawerTrigger>

      <DrawerContent className="h-full w-full max-w-sm sm:max-w-md bg-neutral-50/98 backdrop-blur-md">
        <DrawerHeader className="border-b border-neutral-200/80 px-6 pb-4 pt-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                <LuSlidersHorizontal className="h-4 w-4" />
              </div>
              <div>
                <DrawerTitle className="text-lg font-bold text-neutral-900">
                  Filter Articles
                </DrawerTitle>
                <DrawerDescription className="text-xs text-neutral-500">
                  Select tags and topics
                </DrawerDescription>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={onClearAll}
                  className="inline-flex h-8 items-center gap-1 px-2.5 rounded-lg text-xs font-semibold text-neutral-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LuRotateCcw className="h-3 w-3" />
                  Reset
                </button>
              )}
              <DrawerClose asChild>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/60 transition-colors"
                >
                  <LuX className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </DrawerClose>
            </div>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <BlogsFilter
            tags={tags}
            activeTags={activeTags}
            onToggleTag={onToggleTag}
            onClearTags={onClearTags}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            showSearch={false}
            variant="drawer"
          />
        </div>

        <DrawerFooter className="border-t border-neutral-200/80 bg-white px-6 py-4 flex flex-row gap-3">
          {activeFilterCount > 0 && (
            <Button
              variant="outline"
              onClick={onClearAll}
              className="h-11 flex-1 rounded-xl text-sm font-semibold"
            >
              Clear All
            </Button>
          )}
          <Button
            onClick={() => onOpenChange(false)}
            className="h-11 flex-1 rounded-xl text-sm font-semibold shadow-md shadow-secondary-200 bg-secondary-600 hover:bg-secondary-700 text-white"
          >
            Show Results
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
